import type { LevelDef } from '@full-elevator/sim';

/**
 * The HUD clock: what is left, not what has gone (v0.8).
 *
 * Every level has always had a time limit, but the HUD counted up, so the
 * limit was invisible until it ended the run. Now the clock counts down to it,
 * and warns twice: yellow with a fifth of the time left, red and blinking with
 * a tenth. On a shift (`survive`) the clock that matters is the shift's, which
 * ends the run as a win long before the level's own limit could.
 *
 * Presentation only. The limits are the levels' own and unchanged; this reads
 * them, it decides nothing.
 */

export type Urgency = 'calm' | 'low' | 'critical';

/** A fifth of the time left turns the clock yellow; a tenth turns it red. */
export const LOW_SHARE = 0.2;
export const CRITICAL_SHARE = 0.1;

export interface Clock {
  readonly remainingMs: number;
  readonly totalMs: number;
  readonly urgency: Urgency;
  /** Short on purpose: on a 320px phone every letter here comes out of the level's name. */
  readonly label: 'time' | 'shift';
}

export function clockFor(
  elapsedMs: number,
  timeLimitMs: number,
  objective: LevelDef['objective'],
): Clock {
  const shift = objective.kind === 'survive';
  const totalMs = shift ? objective.untilMs : timeLimitMs;
  const remainingMs = Math.max(0, totalMs - elapsedMs);
  return {
    remainingMs,
    totalMs,
    urgency: urgencyOf(remainingMs, totalMs),
    label: shift ? 'shift' : 'time',
  };
}

export function urgencyOf(remainingMs: number, totalMs: number): Urgency {
  if (remainingMs <= totalMs * CRITICAL_SHARE) return 'critical';
  if (remainingMs <= totalMs * LOW_SHARE) return 'low';
  return 'calm';
}

/**
 * On a level graded on time, how many stars a win could still bring.
 *
 * The clock used to say this by its colour. The countdown's yellow would read
 * the same as two-star gold, so the stars sit under the clock instead and go
 * out one at a time. A win always brings at least one.
 */
export function starsInReach(elapsedMs: number, three: number, two: number): 1 | 2 | 3 {
  if (elapsedMs <= three) return 3;
  if (elapsedMs <= two) return 2;
  return 1;
}
